/**
 * Implements software requirements: H1SR-71, H1SR-93, H1SR-94
 *
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-93/User-can-select-a-surgeon-for-a-case
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-71/When-a-user-creates-their-first-case-they-are-required-to-fill-out-User-preferences-before-being-able-to-proceed-with-the-case
 * @link https://formuslabs.youtrack.cloud/issue/H1SR-94/Surgeon-preferences-are-inherited-into-Surgical-Specifications-and-are-able-to-be-viewed-edited
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getPreferences, NoSurgicalPreferencesError, type THAPreferences } from '@/api/preferences';
import { cloneDeep, isEqual } from 'lodash';
import { validateInputs, validators } from '@/stores/caseSettings/validation';
import assert from 'assert';
import type { CaseSettingsOptions, SurgeonOption } from '@/api/caseSettings';
import { DataState, withStateLoading, withStateSaving } from '@/util/dataState';
import type {
    CasePatient,
    CaseSettingsInputs,
    CaseSettingsState,
    CaseSpecifications,
    CaseStudy,
} from '@/stores/caseSettings/types';
import {
    getSurgeonPreferences,
    loadCaseSettings,
    loadCaseSettingsOptions,
    saveCaseSettings,
} from '@/stores/caseSettings/services';
import { makeInitialState } from '@/stores/caseSettings/initialState';
import { isNotEmpty } from '@/util/validation';
import { useUserStore } from '@/stores/userStore';
import { verify } from '@/util/verify';
import { useCaseSyncStore } from '@/stores/useCaseSyncStore';
import { AxiosError, type AxiosResponse, HttpStatusCode } from 'axios';
import { client } from '@/api/http';

export const useCaseSettings = defineStore('caseSettings', () => {

    const _state = ref<CaseSettingsState>(makeInitialState());

    const isValid = computed((): boolean => {
        return validateInputs(validators, _state.value.case);
    });
    const isDirty = computed((): boolean => {
        const makeComparable = (inputs: CaseSettingsInputs): CaseSettingsInputs => {
            return {
                ...cloneDeep(inputs),
                study: null,
            };
        };

        return !isEqual(
            makeComparable(_state.value._lastSavedCase),
            makeComparable(_state.value.case),
        );
    });

    const isLoaded = computed(() => _state.value.state !== DataState.Loading);
    const isError = computed(() => _state.value.state === DataState.Error);
    const isLoading = computed(() => _state.value.state === DataState.Loading);
    const isSaving = computed(() => _state.value.state === DataState.Saving);
    const isValidating = computed(() => _state.value.state === DataState.Validating);

    const isCaseDeemedUnsuitable = computed((): boolean => {
        const { isCaseNew, isCaseProcessing } = useCaseSyncStore();
        const noSurgeon = _state.value.case.specifications.surgeon.selected === null;
        const isExistingCase = !!_state.value.case.id;
        return isExistingCase && noSurgeon && !isCaseNew && !isCaseProcessing;
    });

    const isCaseReadonly = computed((): boolean => {
        return isCaseDeemedUnsuitable.value;
    });

    const isSpecificationsEnabled = computed((): boolean => {
        return _state.value.case.specifications.surgeon.selected !== null;
    });

    const patient = computed((): CasePatient => {
        return _state.value.case.patient;
    });

    const specifications = computed((): CaseSpecifications => {
        return _state.value.case.specifications;
    });

    const study = computed((): CaseStudy | null => {
        return _state.value.case.study;
    });

    const hasStudy = computed((): boolean => {
        return !!study.value;
    });

    const hasEnteredPatientData = computed((): boolean => {
        return _state.value.case.patient && arePatientBasicDetailsCompleted(_state.value.case.patient);
    });

    const currentSurgeon = computed((): SurgeonOption | null => {
        return (
            specifications.value.surgeon.options.find(
                (s) => s.id === specifications.value.surgeon.selected,
            ) ?? null
        );
    });

    const canUnassignSurgeon = computed((): boolean => {
        const userStore = useUserStore();
        const currentUserId = userStore.user?.id;
        const caseOwner = _state.value.case.owner_id;
        return (
            userStore.isAdmin ||
            userStore.isOrgAdmin ||
            (userStore.isSales && !!caseOwner && !!currentUserId && caseOwner === currentUserId)
        );
    });

    // Actions
    function hasFieldError(field: string): boolean {
        const validator = validators[field];
        assert.ok(!!validator, `no validator for ${field}`);
        return !validator(_state.value.case);
    }

    async function loadNewCase() {
        await withStateLoading(_state.value, async () => {
            const options = await loadCaseSettingsOptions();
            const surgeonId = options.surgeons[0].id;
            const preferences = await getSurgeonPreferences(surgeonId);

            _setInitialNewState(options, surgeonId, preferences);
        });
    }

    async function loadExistingCase(caseId: number) {
        await withStateLoading(_state.value, async () => {
            const caseSettings = await loadCaseSettings(caseId);
            _setInitialEditState(caseSettings);
        });
    }

    /**
     * Persists the case. Caller (VM) is responsible for deciding whether to
     * save (validation, feature gates) and for orchestrating any sibling
     * persistence (e.g. surgical guide).
     */
    async function save(isNewCase: boolean, webComponentVersion: string) {
        await withStateSaving(_state.value, async () => {
            _state.value.case.id = await saveCaseSettings(isNewCase, _state.value.case, webComponentVersion);
            _setLastSavedCase();
        });
    }

    /**
     * Override the user case settings with those of the selected surgeon.
     * Orchestration of sibling stores (surgical guide) happens in the VM.
     */
    async function onUpdateSurgeon(surgeonId: number) {
        _state.value.case.specifications.surgeon.selected = surgeonId;
        _state.value.case.specifications.surgeon.error = false;

        try {
            const preferences = await getPreferences(surgeonId);
            _setSurgeonSpecifications(preferences);
        } catch (err) {
            if (err instanceof NoSurgicalPreferencesError) {
                _state.value.case.specifications.surgeon.error = true;
                return;
            } else {
                throw err;
            }
        }
    }

    async function unassignSurgeonAction(): Promise<void> {
        await withStateSaving(_state.value, async () => {
            const caseId = verify(_state.value.case.id, 'Case ID is required to unassign surgeon');

            await _unassignSurgeon(caseId);

            // update surgeon changes
            _state.value.case.specifications.surgeon.selected = null;
            _state.value.case.specifications.surgeon.error = false;
        });

        if (isError.value) {
            _state.value.case.specifications.surgeon.error = true;
            return;
        }
    }

    function _setInitialNewState(
        options: CaseSettingsOptions,
        surgeonId: number,
        preferences: THAPreferences | null,
    ) {
        // only assign surgeon and surgeon preferences when there is only 1 option
        const assignSurgeon = options.surgeons.length === 1;

        _state.value.case.specifications.stems.options = [...options.stems];
        _state.value.case.specifications.surgeon.options = [...options.surgeons];
        _state.value.case.specifications.surgeon.selected = assignSurgeon ? surgeonId : null;

        if (assignSurgeon && preferences) {
            _setSurgeonSpecifications(preferences);
        } else {
            console.warn('Surgeon does not have preferences');
        }

        _setLastSavedCase();
    }

    function _setInitialEditState(caseSettings: CaseSettingsInputs) {
        _state.value.case = caseSettings;
        _setLastSavedCase();
    }

    function _setSurgeonSpecifications(preferences: THAPreferences): void {
        _state.value.case.specifications.stems.selected = [...preferences.stems];
        _state.value.case.specifications.stems.preferredSystem = preferences.preferredSystem;
        _state.value.case.specifications.cup = {
            inclinationAngle: preferences.cupInclinationAngle,
            fitMethod: preferences.cupFitMethod,
            anteversion: preferences.cupAnteversionMode,
            anteversionAngle: preferences.cupAnteversionAngle,
            alignment: preferences.cupAlignMode,
        };
    }

    function _setLastSavedCase() {
        _state.value._lastSavedCase = cloneDeep(_state.value.case);
    }

    function $reset() {
        _state.value = makeInitialState();
    }

    return {
        state: computed(() => _state.value),
        case: computed(() => _state.value.case),
        error: computed(() => _state.value.error),
        isValid,
        isDirty,

        isLoaded,
        isError,
        isLoading,
        isSaving,
        isValidating,
        isCaseDeemedUnsuitable,
        isCaseReadonly,
        isSpecificationsEnabled,
        patient,
        specifications,
        study,
        hasStudy,
        hasEnteredPatientData,
        currentSurgeon,
        canUnassignSurgeon,

        hasFieldError,
        loadNewCase,
        loadExistingCase,
        save,
        onUpdateSurgeon,
        unassignSurgeon: unassignSurgeonAction,
        $reset,
    };
});

/** @return true if the basic data of the patient is completed (sex, date of birth, and family, given name) */
function arePatientBasicDetailsCompleted(patient: CasePatient): boolean {
    return (
        isNotEmpty(patient.sex) &&
        isNotEmpty(patient.dateOfBirth) &&
        isNotEmpty(patient.firstName) &&
        isNotEmpty(patient.familyName)
    );
}

async function _unassignSurgeon(caseId: number): Promise<void> {
    const { status, data }: AxiosResponse = await client.delete(`/cases/${caseId}/surgeon`);

    if (status === HttpStatusCode.NoContent) {
        return data;
    }

    throw new AxiosError(`Failed to delete user surgeons: ${data?.detail}`);
}

